'use client';
import React, { useEffect, useState } from 'react';
import { CalendarDays, DollarSign, AlertCircle, CheckCircle2, TrendingUp, TrendingDown, Loader2 } from 'lucide-react';
import { bookingsService } from '@/lib/supabase/bookingDataService';
import Icon from '@/components/ui/AppIcon';


export default function AdminKpiBentoGrid() {
  const [stats, setStats] = useState<{
    todayCount: number;
    todayConfirmed: number;
    todayInProgress: number;
    todayPending: number;
    weeklyRevenue: number;
    weeklyDeposits: number;
    weeklyFull: number;
    pendingDepositsCount: number;
    completedThisMonth: number;
    cancelledThisMonth: number;
  } | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    bookingsService.getKpiStats()
      .then(setStats)
      .catch(console.error)
      .finally(() => setIsLoading(false));
  }, []);

  const kpis = stats ? [
    {
      id: 'kpi-today',
      label: "Today's Bookings",
      value: String(stats.todayCount),
      unit: 'appointments',
      trend: `${stats.todayConfirmed} confirmed · ${stats.todayInProgress} in progress`,
      trendUp: stats.todayCount > 0,
      icon: CalendarDays,
      iconBg: 'bg-blue-500/10',
      iconColor: 'text-blue-400',
      cardBg: '',
      hero: true,
      detail: `${stats.todayConfirmed} confirmed · ${stats.todayInProgress} in progress · ${stats.todayPending} pending`,
    },
    {
      id: 'kpi-revenue',
      label: 'Weekly Revenue',
      value: `$${stats.weeklyRevenue.toLocaleString()}`,
      unit: 'this week',
      trend: `$${stats.weeklyDeposits.toLocaleString()} deposits · $${stats.weeklyFull.toLocaleString()} full`,
      trendUp: stats.weeklyRevenue > 0,
      icon: DollarSign,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      cardBg: '',
      hero: false,
      detail: `$${stats.weeklyDeposits.toLocaleString()} in deposits · $${stats.weeklyFull.toLocaleString()} full payments`,
    },
    {
      id: 'kpi-pending',
      label: 'Pending Deposits',
      value: String(stats.pendingDepositsCount),
      unit: 'unconfirmed',
      trend: stats.pendingDepositsCount > 0 ? '⚠ Needs attention' : 'All clear',
      trendUp: stats.pendingDepositsCount === 0,
      icon: AlertCircle,
      iconBg: 'bg-amber-500/10',
      iconColor: 'text-amber-400',
      cardBg: stats.pendingDepositsCount > 0 ? 'bg-amber-500/5 border-amber-500/20' : '',
      hero: false,
      detail: stats.pendingDepositsCount > 0 ? `${stats.pendingDepositsCount} bookings awaiting payment` : 'No pending deposits',
    },
    {
      id: 'kpi-completion',
      label: 'Completed This Month',
      value: String(stats.completedThisMonth),
      unit: 'this month',
      trend: `${stats.cancelledThisMonth} cancelled`,
      trendUp: stats.completedThisMonth > 0,
      icon: CheckCircle2,
      iconBg: 'bg-emerald-500/10',
      iconColor: 'text-emerald-400',
      cardBg: '',
      hero: false,
      detail: `${stats.completedThisMonth} completed · ${stats.cancelledThisMonth} cancelled`,
    },
  ] : [];

  if (isLoading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className={`bg-card border border-border rounded-xl p-5 ${i === 1 ? 'xl:col-span-2' : ''}`}>
            <div className="flex items-center justify-center h-20">
              <Loader2 size={20} className="animate-spin text-muted-foreground" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 2xl:grid-cols-4 gap-4">
      {kpis?.map((kpi) => {
        const Icon = kpi?.icon;
        const isHero = kpi?.hero;

        return (
          <div
            key={kpi?.id}
            className={`bg-card border rounded-xl p-5 transition-all duration-200 hover:shadow-card ${
              isHero ? 'xl:col-span-2' : ''
            } ${kpi?.cardBg || 'border-border'}`}
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs font-semibold tracking-wide text-muted-foreground uppercase">{kpi?.label}</p>
                <p className="text-xs text-muted-foreground/60 mt-0.5">{kpi?.unit}</p>
              </div>
              <div className={`w-9 h-9 rounded-xl ${kpi?.iconBg} flex items-center justify-center flex-shrink-0`}>
                <Icon size={18} className={kpi?.iconColor} />
              </div>
            </div>
            <p className={`font-extrabold text-foreground tabular-nums mb-1 ${isHero ? 'text-4xl' : 'text-3xl'}`}>
              {kpi?.value}
            </p>
            <div className="flex items-center gap-1.5 mb-3">
              {kpi?.trendUp ? (
                <TrendingUp size={13} className="text-emerald-400" />
              ) : (
                <TrendingDown size={13} className="text-amber-400" />
              )}
              <span className={`text-xs font-medium ${kpi?.trendUp ? 'text-emerald-400' : 'text-amber-400'}`}>
                {kpi?.trend}
              </span>
            </div>
            <p className="text-xs text-muted-foreground/70">{kpi?.detail}</p>
          </div>
        );
      })}
    </div>
  );
}